import React, { InputHTMLAttributes, forwardRef, useId } from 'react';
import { cn } from '@/lib/utils';

type CheckboxSize = 'sm' | 'md' | 'lg';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
    label?: string;
    description?: string;
    error?: string;
    checkboxSize?: CheckboxSize;
    containerClassName?: string;
}

const boxSize: Record<CheckboxSize, string> = {
    sm: 'w-4 h-4',
    md: 'w-[18px] h-[18px]',
    lg: 'w-5 h-5',
};

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps & { indeterminate?: boolean }>(
    ({
        className = '',
        label,
        description,
        error,
        checkboxSize = 'md',
        containerClassName = '',
        disabled,
        indeterminate,
        id: idProp,
        name: nameProp,
        ...props
    }, ref) => {
        const generatedId = useId();
        const checkboxId = idProp ?? generatedId;
        const checkboxName = nameProp ?? checkboxId;
        const innerRef = React.useRef<HTMLInputElement>(null);

        React.useImperativeHandle(ref, () => innerRef.current!);

        React.useEffect(() => {
            if (innerRef.current) {
                innerRef.current.indeterminate = !!indeterminate;
            }
        }, [indeterminate]);

        return (
            <div className={containerClassName}>
                <label
                    htmlFor={checkboxId}
                    className={cn('checkbox-wrap', disabled && 'cursor-not-allowed opacity-50')}
                >
                    <input
                        ref={innerRef}
                        id={checkboxId}
                        name={checkboxName}
                        type="checkbox"
                        disabled={disabled}
                        className={cn('checkbox-input', className)}
                        {...props}
                    />
                    <div className={cn('checkbox-box', boxSize[checkboxSize])} aria-hidden="true" />
                    {(label || description) && (
                        <span>
                            {label && <span className="checkbox-text block">{label}</span>}
                            {description && <span className="input-hint block">{description}</span>}
                        </span>
                    )}
                </label>
                {error && <p className="input-error mt-1">{error}</p>}
            </div>
        );
    }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
